/* eslint-disable prefer-spread */
/* eslint-disable max-lines */
import {
  Alert,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Modal,
  Row,
} from 'reactstrap';
import { useEffect, useState } from 'react';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { safeSetTextField } from '../../../helpers/pdfFieldHelpers';
import SignatureCanvas from 'react-signature-canvas';
import NumberFormat from 'react-number-format';
import moment from 'moment';
import HireDocuments from '../../../services/HireDocuments';
import { toast } from 'react-toastify';
import InputMask from 'react-input-mask';
import ReactDatetime from 'react-datetime';
import { getCurrentW4Config, W4_CURRENT_FORM_YEAR } from '../../../helpers/w4FormConfig';

type SignDocumentModalProps = {
  isOpen: boolean;
  token: string;
  currentUser: any;
  toggleModal: (e?: any) => void;
  saveModal: (e?: any) => void;
};

const SignDocumentModal = ({ isOpen, token, currentUser, toggleModal, saveModal }: SignDocumentModalProps) => {
  const [pdf, setPdf] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [ssn, setSsn] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [married, setMarried] = useState('');
  const [amounts, setAmounts] = useState<any[]>([]);
  const [employerName, setEmployerName] = useState(
    currentUser?.school?.name && currentUser?.school?.address
      ? `${currentUser?.school?.name}, ${currentUser?.school?.address}`
      : '',
  );
  const [date, setDate] = useState('');
  const [ein, setEin] = useState(currentUser?.school?.federalTaxId || '');
  const [sigPad, setSigPad] = useState<any>();
  const [signature, setSignature] = useState<any>();
  const [signatureDate, setSignatureDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<any>();
  const [pdfSignedBlob, setPdfSignedBlob] = useState<any>();

  const config = getCurrentW4Config();
  const pdfUrl = config.pdfUrl;
  const coordinatesSign = config.coordinatesSign;
  const coordinatesDate = {
    ...config.coordinatesDate,
    color: config.coordinatesDate.color || rgb(0, 0, 0),
  };

  useEffect(() => {
    setSignatureDate(moment().format('MM/DD/YYYY'));
  }, []);

  useEffect(() => {
    if (isOpen) createPdf();
    setSignatureDate(moment().format('MM/DD/YYYY'));
  }, [isOpen, sigPad]);

  const setNewAmounts = async (i: number, v: number) => {
    const newAmounts = JSON.parse(JSON.stringify(amounts));
    const isExists = newAmounts?.find((n: any) => n?.i === i);
    if (isExists) {
      isExists.v = v;
      return setAmounts(newAmounts);
    }
    newAmounts.push({
      i,
      v,
    });
    setAmounts(newAmounts);
  };

  const readOnlyFields = (pdfForm: any) => {
    try {
      const fieldNames = pdfForm.getFields();
      for (const f of fieldNames) {
        const type = f.constructor.name;
        const fieldName = f.getName();
        if (type === 'PDFTextField') {
          pdfForm.getTextField(fieldName).enableReadOnly();
        }
        if (type === 'PDFDropdown') {
          pdfForm.getDropdown(fieldName).enableReadOnly();
        }
        if (type === 'PDFCheckBox') {
          pdfForm.getCheckBox(fieldName).enableReadOnly();
        }
      }
    } catch (e) {
      console.log(e);
    }
  };

  const createPdf = async () => {
    try {
      const arrayBuffer = await fetch(pdfUrl).then((res) => res.arrayBuffer());
      setPdfBlob(arrayBuffer);
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const pdfForm = pdfDoc.getForm();
      const pages = pdfDoc.getPages();
      const firstPage = pages[0];
      const timesRomanFont = await pdfDoc.embedFont(StandardFonts.TimesRoman);

      if (firstPage && sigPad) {
        const img = sigPad?.getTrimmedCanvas()?.toDataURL('image/png');
        setSignature(img);
        const pngImage = await pdfDoc.embedPng(img);
        firstPage.drawImage(pngImage, coordinatesSign);
        firstPage.drawText(signatureDate, {
          x: coordinatesDate.x,
          y: coordinatesDate.y,
          size: coordinatesDate.size,
          font: timesRomanFont,
          color: rgb(0, 0, 0),
        });
      }

      if (config.useFormFields) {
        const fieldNames = pdfForm.getFields().map((f) => f.getName());
        if (fieldNames.length > 0) {
          readOnlyFields(pdfForm);
          const idx = config.fieldIndices;
          const emp = idx?.employerName ?? 15;
          const dat = idx?.date ?? 16;
          const ei = idx?.ein ?? 17;
          const getAmountIdx = idx?.getAmountFormIndex ?? ((i: number) => i);

          const setText = (index: number, value: string | number | undefined) => {
            const name = fieldNames[index];
            if (!name) return;
            safeSetTextField(pdfForm, name, value != null ? String(value) : '');
          };
          const setCheck = (index: number, check: boolean) => {
            const name = fieldNames[index];
            if (name) {
              try {
                const field = pdfForm.getCheckBox(name);
                field.enableReadOnly();
                if (check) field.check();
              } catch {
                void 0;
              }
            }
          };

          setText(0, firstName);
          setText(1, lastName);
          setText(2, address);
          setText(3, city);
          setText(4, ssn);
          setText(emp, employerName);
          setText(dat, date ? moment(date).format('MM/DD/yyyy') : '');
          setText(ei, ein);
          setCheck(5, married === '1');
          setCheck(6, married === '2');
          setCheck(7, married === '3');
          if (idx?.step3Total != null && fieldNames[idx.step3Total]) {
            const a9 = amounts.find((a) => a.i === 9)?.v ?? 0;
            const a10 = amounts.find((a) => a.i === 10)?.v ?? 0;
            const step3Total = Number(a9) + Number(a10);
            setText(idx.step3Total, String(step3Total));
          }
          for (const n of amounts) {
            if (n?.i == null) continue;
            const formIndex = getAmountIdx(n.i);
            setText(formIndex, String(n?.v ?? 0));
          }
        }
      }

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes as any], { type: 'application/pdf' });
      setPdfSignedBlob(blob);
      const docUrl = URL.createObjectURL(blob);
      setPdf(docUrl);
    } catch (e) {
      console.log(e);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);

    const fields = {
      firstName,
      lastName,
      ssn,
      address,
      city,
      married,
      amounts,
      employerName,
      date,
      ein,
    };
    const body = {
      type: 'W4',
      userId: currentUser?.userId || '',
      userName: currentUser?.userName || '',
      userToken: token,
      pdf: pdfBlob,
      pdfUrl,
      pdfSigned: pdfSignedBlob,
      signature,
      options: {
        type: 'W4',
        formYear: W4_CURRENT_FORM_YEAR,
        docUrl: pdf,
        fields,
        coordinatesSign,
        coordinatesDate,
        signPng: signature,
        signatureDate: moment().format('YYYY-MM-DD'),
      },
    };

    try {
      await HireDocuments('').create(body);
      toast.success(`Saved successfully!`);
      saveModal(e);
    } catch (e: any) {
      console.log(e);
      const message = String(e?.response?.data?.message || 'Create error!');
      toast.error(message);
    }

    setLoading(false);
  };

  return (
    <>
      <Modal className="modal-dialog-centered" size="xl" isOpen={isOpen} toggle={(e: any) => toggleModal(e)}>
        <div className="modal-header pb-1">
          <h5 className="modal-title">Document Signing</h5>
          <button
            aria-label="Close"
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={(e) => toggleModal(e)}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <div className="modal-body pt-0">
          <Form
            onBlur={createPdf}
            onSubmit={(e) => {
              handleSubmit(e);
              toggleModal(e);
            }}
          >
            <Row className="mt-2">
              <Col md="6">
                <Row>
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label" htmlFor="input-name">
                        First name and middle initial
                      </label>
                      <Input
                        className="form-control-alternative"
                        id="input-name"
                        placeholder="First name and middle initial"
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label" htmlFor="input-name">
                        Last name
                      </label>
                      <Input
                        className="form-control-alternative"
                        id="input-name"
                        placeholder="Last Name"
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label" htmlFor="input-name">
                        Social security number
                      </label>
                      <Input
                        className="form-control-alternative"
                        type="text"
                        value={ssn}
                        onChange={(e) => setSsn(e.target.value.replaceAll('-', ''))}
                        required={true}
                        tag={InputMask}
                        mask="999-99-9999"
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label" htmlFor="input-name">
                        Address
                      </label>
                      <Input
                        className="form-control-alternative"
                        id="input-name"
                        placeholder="Address"
                        type="text"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="12">
                    <FormGroup>
                      <label className="form-control-label" htmlFor="input-name">
                        City or Town, State, and ZIP Code
                      </label>
                      <Input
                        className="form-control-alternative"
                        id="input-name"
                        placeholder="City"
                        type="text"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md="12">
                    <FormGroup check>
                      <Label check>
                        <Input
                          name="maried"
                          type="radio"
                          value={'1'}
                          checked={married === '1'}
                          onChange={(e) => setMarried(e.target.value)}
                        />{' '}
                        Single or Married filing separately
                      </Label>
                    </FormGroup>
                  </Col>
                  <Col md="12">
                    <FormGroup check>
                      <Label check>
                        <Input
                          name="maried"
                          type="radio"
                          value={'2'}
                          checked={married === '2'}
                          onChange={(e) => setMarried(e.target.value)}
                        />{' '}
                        Married filing jointly or Qualifying surviving spouse
                      </Label>
                    </FormGroup>
                  </Col>
                  <Col md="12" className="mb-2">
                    <FormGroup check>
                      <Label check>
                        <Input
                          name="maried"
                          type="radio"
                          value={'3'}
                          checked={married === '3'}
                          onChange={(e) => setMarried(e.target.value)}
                        />{' '}
                        Head of household
                      </Label>
                    </FormGroup>
                  </Col>
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label">
                        {`Multiply the number of qualifying children under age 17 by $${
                          W4_CURRENT_FORM_YEAR >= 2026 ? '2,200' : '2,000'
                        }`}
                      </label>
                      <Input
                        type="text"
                        thousandsGroupStyle="thousand"
                        placeholder="$0"
                        prefix="$"
                        decimalSeparator="."
                        displayType="input"
                        thousandSeparator={true}
                        allowNegative={false}
                        decimalScale={2}
                        tag={NumberFormat}
                        value={amounts?.find((a) => a.i === 9)?.v}
                        onValueChange={(e: any) => setNewAmounts(9, e.floatValue)}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label">Multiply the number of other dependents by $500</label>
                      <Input
                        type="text"
                        thousandsGroupStyle="thousand"
                        placeholder="$0"
                        prefix="$"
                        decimalSeparator="."
                        displayType="input"
                        thousandSeparator={true}
                        allowNegative={false}
                        decimalScale={2}
                        tag={NumberFormat}
                        value={amounts?.find((a) => a.i === 10)?.v}
                        onValueChange={(e: any) => setNewAmounts(10, e.floatValue)}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label">Step 3 - Nº 3</label>
                      <Input
                        type="text"
                        thousandsGroupStyle="thousand"
                        placeholder="$0"
                        prefix="$"
                        decimalSeparator="."
                        displayType="input"
                        thousandSeparator={true}
                        allowNegative={false}
                        decimalScale={2}
                        tag={NumberFormat}
                        value={amounts?.find((a) => a.i === 11)?.v}
                        onValueChange={(e: any) => setNewAmounts(11, e.floatValue)}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label">Step 4 - 4(a)</label>
                      <Input
                        type="text"
                        thousandsGroupStyle="thousand"
                        placeholder="$0"
                        prefix="$"
                        decimalSeparator="."
                        displayType="input"
                        thousandSeparator={true}
                        allowNegative={false}
                        decimalScale={2}
                        tag={NumberFormat}
                        value={amounts?.find((a) => a.i === 12)?.v}
                        onValueChange={(e: any) => setNewAmounts(12, e.floatValue)}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label">Step 4 - 4(b)</label>
                      <Input
                        type="text"
                        thousandsGroupStyle="thousand"
                        placeholder="$0"
                        prefix="$"
                        decimalSeparator="."
                        displayType="input"
                        thousandSeparator={true}
                        allowNegative={false}
                        decimalScale={2}
                        tag={NumberFormat}
                        value={amounts?.find((a) => a.i === 13)?.v}
                        onValueChange={(e: any) => setNewAmounts(13, e.floatValue)}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label">Step 4 - 4(c)</label>
                      <Input
                        type="text"
                        thousandsGroupStyle="thousand"
                        placeholder="$0"
                        prefix="$"
                        decimalSeparator="."
                        displayType="input"
                        thousandSeparator={true}
                        allowNegative={false}
                        decimalScale={2}
                        tag={NumberFormat}
                        value={amounts?.find((a) => a.i === 14)?.v}
                        onValueChange={(e: any) => setNewAmounts(14, e.floatValue)}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="12 mt-2">
                    <label className="form-control-label mr-2" htmlFor="input-name">
                      Signature
                    </label>
                  </Col>
                  <Col lg="6">
                    <div>
                      <SignatureCanvas
                        penColor="blue"
                        canvasProps={{ width: 500, height: 200, className: 'sig-canvas' }}
                        ref={(ref) => setSigPad(ref)}
                        onEnd={createPdf}
                      />
                    </div>
                  </Col>
                  <Col lg="12">
                    <Button onClick={() => sigPad?.clear()} color="warning" size="sm" className="mt-1 mb-2">
                      Clear
                    </Button>
                  </Col>
                  <Col lg="12">
                    <FormGroup>
                      <label className="form-control-label" htmlFor="input-name">
                        Step 5 - Employer’s name and address
                      </label>
                      <Input
                        className="form-control-alternative"
                        type="textarea"
                        value={employerName}
                        onChange={(e) => setEmployerName(e.target.value)}
                        disabled
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label">First date of employment</label>
                      <InputGroup className="input-group-alternative">
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="ni ni-calendar-grid-58" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <ReactDatetime
                          inputProps={{
                            placeholder: 'Date',
                          }}
                          onChange={(newValue) => {
                            setDate(`${newValue}`);
                          }}
                          timeFormat={false}
                          value={`${date}`}
                          dateFormat={'MM/DD/yyyy'}
                          closeOnSelect={true}
                        />
                      </InputGroup>
                    </FormGroup>
                  </Col>
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label" htmlFor="input-name">
                        EIN
                      </label>
                      <Input
                        className="form-control-alternative"
                        id="input-name"
                        placeholder=""
                        type="text"
                        value={ein}
                        onChange={(e) => setEin(e.target.value)}
                        required
                        disabled
                      />
                    </FormGroup>
                  </Col>

                  <Col lg="12" className="mt-2 mb-2">
                    <h3>Step 2(b) — Multiple Jobs Worksheet </h3>
                  </Col>
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label">Two Jobs - 1</label>
                      <Input
                        type="text"
                        thousandsGroupStyle="thousand"
                        placeholder="$0"
                        prefix="$"
                        decimalSeparator="."
                        displayType="input"
                        thousandSeparator={true}
                        allowNegative={false}
                        decimalScale={2}
                        tag={NumberFormat}
                        value={amounts?.find((a) => a.i === 18)?.v}
                        onValueChange={(e: any) => setNewAmounts(18, e.floatValue)}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label">Three Jobs - 2a</label>
                      <Input
                        type="text"
                        thousandsGroupStyle="thousand"
                        placeholder="$0"
                        prefix="$"
                        decimalSeparator="."
                        displayType="input"
                        thousandSeparator={true}
                        allowNegative={false}
                        decimalScale={2}
                        tag={NumberFormat}
                        value={amounts?.find((a) => a.i === 19)?.v}
                        onValueChange={(e: any) => setNewAmounts(19, e.floatValue)}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label">Three Jobs - 2b</label>
                      <Input
                        type="text"
                        thousandsGroupStyle="thousand"
                        placeholder="$0"
                        prefix="$"
                        decimalSeparator="."
                        displayType="input"
                        thousandSeparator={true}
                        allowNegative={false}
                        decimalScale={2}
                        tag={NumberFormat}
                        value={amounts?.find((a) => a.i === 20)?.v}
                        onValueChange={(e: any) => setNewAmounts(20, e.floatValue)}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label">Three Jobs - 2c</label>
                      <Input
                        type="text"
                        thousandsGroupStyle="thousand"
                        placeholder="$0"
                        prefix="$"
                        decimalSeparator="."
                        displayType="input"
                        thousandSeparator={true}
                        allowNegative={false}
                        decimalScale={2}
                        tag={NumberFormat}
                        value={amounts?.find((a) => a.i === 21)?.v}
                        onValueChange={(e: any) => setNewAmounts(21, e.floatValue)}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label">Number of pay periods - 3</label>
                      <Input
                        type="text"
                        thousandsGroupStyle="thousand"
                        placeholder="$0"
                        prefix="$"
                        decimalSeparator="."
                        displayType="input"
                        thousandSeparator={true}
                        allowNegative={false}
                        decimalScale={2}
                        tag={NumberFormat}
                        value={amounts?.find((a) => a.i === 22)?.v}
                        onValueChange={(e: any) => setNewAmounts(22, e.floatValue)}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label">Divide - 4</label>
                      <Input
                        type="text"
                        thousandsGroupStyle="thousand"
                        placeholder="$0"
                        prefix="$"
                        decimalSeparator="."
                        displayType="input"
                        thousandSeparator={true}
                        allowNegative={false}
                        decimalScale={2}
                        tag={NumberFormat}
                        value={amounts?.find((a) => a.i === 23)?.v}
                        onValueChange={(e: any) => setNewAmounts(23, e.floatValue)}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="12" className="mt-2 mb-2">
                    <h3>Step 4(b) — Deductions Worksheet</h3>
                  </Col>
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label">
                        {W4_CURRENT_FORM_YEAR >= 2026 ? '1a - Qualified tips' : 'Step 4(b) - 1'}
                      </label>
                      <Input
                        type="text"
                        thousandsGroupStyle="thousand"
                        placeholder="$0"
                        prefix="$"
                        decimalSeparator="."
                        displayType="input"
                        thousandSeparator={true}
                        allowNegative={false}
                        decimalScale={2}
                        tag={NumberFormat}
                        value={amounts?.find((a) => a.i === 24)?.v}
                        onValueChange={(e: any) => setNewAmounts(24, e.floatValue)}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label">
                        {W4_CURRENT_FORM_YEAR >= 2026 ? '1b - Qualified overtime' : 'Step 4(b) - 2'}
                      </label>
                      <Input
                        type="text"
                        thousandsGroupStyle="thousand"
                        placeholder="$0"
                        prefix="$"
                        decimalSeparator="."
                        displayType="input"
                        thousandSeparator={true}
                        allowNegative={false}
                        decimalScale={2}
                        tag={NumberFormat}
                        value={amounts?.find((a) => a.i === 25)?.v}
                        onValueChange={(e: any) => setNewAmounts(25, e.floatValue)}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label">
                        {W4_CURRENT_FORM_YEAR >= 2026 ? '1c - Vehicle loan interest' : 'Step 4(b) - 3'}
                      </label>
                      <Input
                        type="text"
                        thousandsGroupStyle="thousand"
                        placeholder="$0"
                        prefix="$"
                        decimalSeparator="."
                        displayType="input"
                        thousandSeparator={true}
                        allowNegative={false}
                        decimalScale={2}
                        tag={NumberFormat}
                        value={amounts?.find((a) => a.i === 26)?.v}
                        onValueChange={(e: any) => setNewAmounts(26, e.floatValue)}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label">
                        {W4_CURRENT_FORM_YEAR >= 2026 ? '2 - Add 1a+1b+1c' : 'Step 4(b) - 4'}
                      </label>
                      <Input
                        type="text"
                        thousandsGroupStyle="thousand"
                        placeholder="$0"
                        prefix="$"
                        decimalSeparator="."
                        displayType="input"
                        thousandSeparator={true}
                        allowNegative={false}
                        decimalScale={2}
                        tag={NumberFormat}
                        value={amounts?.find((a) => a.i === 27)?.v}
                        onValueChange={(e: any) => setNewAmounts(27, e.floatValue)}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label">
                        {W4_CURRENT_FORM_YEAR >= 2026 ? '3a - Seniors 65+ ($6,000)' : 'Step 4(b) - 5'}
                      </label>
                      <Input
                        type="text"
                        thousandsGroupStyle="thousand"
                        placeholder="$0"
                        prefix="$"
                        decimalSeparator="."
                        displayType="input"
                        thousandSeparator={true}
                        allowNegative={false}
                        decimalScale={2}
                        tag={NumberFormat}
                        value={amounts?.find((a) => a.i === 28)?.v}
                        onValueChange={(e: any) => setNewAmounts(28, e.floatValue)}
                      />
                    </FormGroup>
                  </Col>
                  {W4_CURRENT_FORM_YEAR >= 2026 && (
                    <>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">3b - Spouse 65+ ($6,000)</label>
                          <Input
                            type="text"
                            thousandsGroupStyle="thousand"
                            placeholder="$0"
                            prefix="$"
                            decimalSeparator="."
                            displayType="input"
                            thousandSeparator={true}
                            allowNegative={false}
                            decimalScale={2}
                            tag={NumberFormat}
                            value={amounts?.find((a) => a.i === 29)?.v}
                            onValueChange={(e: any) => setNewAmounts(29, e.floatValue)}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">4 - Add 3a+3b</label>
                          <Input
                            type="text"
                            thousandsGroupStyle="thousand"
                            placeholder="$0"
                            prefix="$"
                            decimalSeparator="."
                            displayType="input"
                            thousandSeparator={true}
                            allowNegative={false}
                            decimalScale={2}
                            tag={NumberFormat}
                            value={amounts?.find((a) => a.i === 30)?.v}
                            onValueChange={(e: any) => setNewAmounts(30, e.floatValue)}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">5 - Student loan, IRA, etc.</label>
                          <Input
                            type="text"
                            thousandsGroupStyle="thousand"
                            placeholder="$0"
                            prefix="$"
                            decimalSeparator="."
                            displayType="input"
                            thousandSeparator={true}
                            allowNegative={false}
                            decimalScale={2}
                            tag={NumberFormat}
                            value={amounts?.find((a) => a.i === 31)?.v}
                            onValueChange={(e: any) => setNewAmounts(31, e.floatValue)}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">6a - Medical/dental</label>
                          <Input
                            type="text"
                            thousandsGroupStyle="thousand"
                            placeholder="$0"
                            prefix="$"
                            decimalSeparator="."
                            displayType="input"
                            thousandSeparator={true}
                            allowNegative={false}
                            decimalScale={2}
                            tag={NumberFormat}
                            value={amounts?.find((a) => a.i === 32)?.v}
                            onValueChange={(e: any) => setNewAmounts(32, e.floatValue)}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">6b - State and local taxes</label>
                          <Input
                            type="text"
                            thousandsGroupStyle="thousand"
                            placeholder="$0"
                            prefix="$"
                            decimalSeparator="."
                            displayType="input"
                            thousandSeparator={true}
                            allowNegative={false}
                            decimalScale={2}
                            tag={NumberFormat}
                            value={amounts?.find((a) => a.i === 33)?.v}
                            onValueChange={(e: any) => setNewAmounts(33, e.floatValue)}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">6c - Mortgage interest</label>
                          <Input
                            type="text"
                            thousandsGroupStyle="thousand"
                            placeholder="$0"
                            prefix="$"
                            decimalSeparator="."
                            displayType="input"
                            thousandSeparator={true}
                            allowNegative={false}
                            decimalScale={2}
                            tag={NumberFormat}
                            value={amounts?.find((a) => a.i === 34)?.v}
                            onValueChange={(e: any) => setNewAmounts(34, e.floatValue)}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">6d - Charities</label>
                          <Input
                            type="text"
                            thousandsGroupStyle="thousand"
                            placeholder="$0"
                            prefix="$"
                            decimalSeparator="."
                            displayType="input"
                            thousandSeparator={true}
                            allowNegative={false}
                            decimalScale={2}
                            tag={NumberFormat}
                            value={amounts?.find((a) => a.i === 35)?.v}
                            onValueChange={(e: any) => setNewAmounts(35, e.floatValue)}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">6e - Other itemized</label>
                          <Input
                            type="text"
                            thousandsGroupStyle="thousand"
                            placeholder="$0"
                            prefix="$"
                            decimalSeparator="."
                            displayType="input"
                            thousandSeparator={true}
                            allowNegative={false}
                            decimalScale={2}
                            tag={NumberFormat}
                            value={amounts?.find((a) => a.i === 36)?.v}
                            onValueChange={(e: any) => setNewAmounts(36, e.floatValue)}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">7 - Add 6a through 6e</label>
                          <Input
                            type="text"
                            thousandsGroupStyle="thousand"
                            placeholder="$0"
                            prefix="$"
                            decimalSeparator="."
                            displayType="input"
                            thousandSeparator={true}
                            allowNegative={false}
                            decimalScale={2}
                            tag={NumberFormat}
                            value={amounts?.find((a) => a.i === 37)?.v}
                            onValueChange={(e: any) => setNewAmounts(37, e.floatValue)}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">8a - Total income</label>
                          <Input
                            type="text"
                            thousandsGroupStyle="thousand"
                            placeholder="$0"
                            prefix="$"
                            decimalSeparator="."
                            displayType="input"
                            thousandSeparator={true}
                            allowNegative={false}
                            decimalScale={2}
                            tag={NumberFormat}
                            value={amounts?.find((a) => a.i === 38)?.v}
                            onValueChange={(e: any) => setNewAmounts(38, e.floatValue)}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">8b - Subtract line 4 from 8a</label>
                          <Input
                            type="text"
                            thousandsGroupStyle="thousand"
                            placeholder="$0"
                            prefix="$"
                            decimalSeparator="."
                            displayType="input"
                            thousandSeparator={true}
                            allowNegative={false}
                            decimalScale={2}
                            tag={NumberFormat}
                            value={amounts?.find((a) => a.i === 39)?.v}
                            onValueChange={(e: any) => setNewAmounts(39, e.floatValue)}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">9 - Limitation threshold</label>
                          <Input
                            type="text"
                            thousandsGroupStyle="thousand"
                            placeholder="$0"
                            prefix="$"
                            decimalSeparator="."
                            displayType="input"
                            thousandSeparator={true}
                            allowNegative={false}
                            decimalScale={2}
                            tag={NumberFormat}
                            value={amounts?.find((a) => a.i === 40)?.v}
                            onValueChange={(e: any) => setNewAmounts(40, e.floatValue)}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">10</label>
                          <Input
                            type="text"
                            thousandsGroupStyle="thousand"
                            placeholder="$0"
                            prefix="$"
                            decimalSeparator="."
                            displayType="input"
                            thousandSeparator={true}
                            allowNegative={false}
                            decimalScale={2}
                            tag={NumberFormat}
                            value={amounts?.find((a) => a.i === 41)?.v}
                            onValueChange={(e: any) => setNewAmounts(41, e.floatValue)}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">11 - Standard deduction</label>
                          <Input
                            type="text"
                            thousandsGroupStyle="thousand"
                            placeholder="$0"
                            prefix="$"
                            decimalSeparator="."
                            displayType="input"
                            thousandSeparator={true}
                            allowNegative={false}
                            decimalScale={2}
                            tag={NumberFormat}
                            value={amounts?.find((a) => a.i === 42)?.v}
                            onValueChange={(e: any) => setNewAmounts(42, e.floatValue)}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">12 - Cash gifts to charities</label>
                          <Input
                            type="text"
                            thousandsGroupStyle="thousand"
                            placeholder="$0"
                            prefix="$"
                            decimalSeparator="."
                            displayType="input"
                            thousandSeparator={true}
                            allowNegative={false}
                            decimalScale={2}
                            tag={NumberFormat}
                            value={amounts?.find((a) => a.i === 43)?.v}
                            onValueChange={(e: any) => setNewAmounts(43, e.floatValue)}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">13 - Add 11+12</label>
                          <Input
                            type="text"
                            thousandsGroupStyle="thousand"
                            placeholder="$0"
                            prefix="$"
                            decimalSeparator="."
                            displayType="input"
                            thousandSeparator={true}
                            allowNegative={false}
                            decimalScale={2}
                            tag={NumberFormat}
                            value={amounts?.find((a) => a.i === 44)?.v}
                            onValueChange={(e: any) => setNewAmounts(44, e.floatValue)}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">14</label>
                          <Input
                            type="text"
                            thousandsGroupStyle="thousand"
                            placeholder="$0"
                            prefix="$"
                            decimalSeparator="."
                            displayType="input"
                            thousandSeparator={true}
                            allowNegative={false}
                            decimalScale={2}
                            tag={NumberFormat}
                            value={amounts?.find((a) => a.i === 45)?.v}
                            onValueChange={(e: any) => setNewAmounts(45, e.floatValue)}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">15 - Enter in Step 4(b)</label>
                          <Input
                            type="text"
                            thousandsGroupStyle="thousand"
                            placeholder="$0"
                            prefix="$"
                            decimalSeparator="."
                            displayType="input"
                            thousandSeparator={true}
                            allowNegative={false}
                            decimalScale={2}
                            tag={NumberFormat}
                            value={amounts?.find((a) => a.i === 46)?.v}
                            onValueChange={(e: any) => setNewAmounts(46, e.floatValue)}
                          />
                        </FormGroup>
                      </Col>
                    </>
                  )}
                </Row>
              </Col>
              <Col md="6" className="mt-4">
                <div style={{ position: 'fixed', minWidth: 530 }}>
                  <Row>
                    <Col lg="12">
                      <Alert style={{ maxWidth: 540 }}>
                        If you would like to view this document in all its detail, please download.
                      </Alert>
                    </Col>
                  </Row>
                  <Row>
                    <Col lg="12">
                      <iframe src={pdf} style={{ minHeight: 500, width: '100%' }} />
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
            <hr />
            <Row>
              <Col md="6">
                <Button color="success" size="md" style={{ minWidth: '100px' }}>
                  Save
                </Button>
              </Col>
            </Row>
          </Form>
        </div>
      </Modal>
    </>
  );
};

export default SignDocumentModal;
